<template>
  <AppLayout>
    <div class="create-group-view">
      <div class="page-header">
        <button @click="goBack" class="back-button">
          <i class="icon-arrow-left"></i>
          Back to Groups
        </button>
        <div class="header-content">
          <h1>Create New Group</h1>
          <p class="subtitle">Set up a new group to organize meals with others</p>
        </div>
      </div>

      <div class="form-container">
        <GroupForm
          :isLoading="isLoading"
          :serverError="error"
          @submit="handleSubmit"
          @cancel="cancel"
        />
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useGroupsStore } from '../../stores/groups';
import { storeToRefs } from 'pinia';
import AppLayout from '../../components/layout/AppLayout.vue';
import GroupForm from '../../components/groups/GroupForm.vue';
import type { CreateGroupData, UpdateGroupData } from '../../types/group';
import { useToast } from '../../composables/useToast';

const router = useRouter();
const groupsStore = useGroupsStore();
const { isLoading, error } = storeToRefs(groupsStore);
const toast = useToast();

const handleSubmit = async (data: CreateGroupData | UpdateGroupData) => {
  try {
    groupsStore.clearError();
    const newGroup = await groupsStore.createGroup(data as CreateGroupData);
    toast.success('Group created!', `"${newGroup.name}" has been successfully created.`);
    router.push(`/groups/${newGroup.id}`);
  } catch (err) {
    console.error('Failed to create group:', err);
    toast.error('Creation failed', 'Failed to create the group. Please try again.');
  }
};

const cancel = () => {
  router.push('/user?tab=groups');
};

const goBack = () => {
  router.push('/user?tab=groups');
};
</script>

<style scoped>
.create-group-view {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s ease;
  margin-bottom: 1rem;
}

.back-button:hover {
  color: #3b82f6;
}

.icon-arrow-left::before {
  content: '←';
  font-size: 1.25rem;
}

.header-content h1 {
  margin: 0;
  color: #1f2937;
  font-size: 2rem;
}

.subtitle {
  margin: 0.5rem 0 0 0;
  color: #6b7280;
  font-size: 1rem;
}

.form-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
